import PublicHeader from "../components/PublicHeader";
import PublicFooter from "../components/PublicFooter";
import { Link } from "react-router-dom";

const Section = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="text-lg font-bold text-luxury-text mb-3 pb-2 border-b border-luxury-border">{title}</h2>
    <div className="text-luxury-secondary text-sm leading-relaxed space-y-3">{children}</div>
  </div>
);

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-luxury-bg">
      <PublicHeader />

      {/* Hero */}
      <section className="pt-28 pb-12 bg-luxury-surface border-b border-luxury-border text-center px-5 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[400px] h-[200px] bg-luxury-gold/5 rounded-full blur-3xl" />
        </div>
        <div className="relative">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-luxury-elevated border border-luxury-gold/30 rounded-full text-luxury-gold text-xs font-semibold mb-6">
            <span className="w-1.5 h-1.5 bg-luxury-gold rounded-full" />
            POPIA Compliant
          </div>
          <h1 className="text-4xl font-extrabold text-luxury-text mb-2">Privacy Policy</h1>
          <p className="text-luxury-muted text-sm">Last updated: June 2025</p>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-14">
        {/* Intro banner */}
        <div className="p-5 bg-luxury-elevated border border-luxury-gold/20 rounded-2xl mb-10">
          <div className="flex items-start gap-3">
            <span className="text-xl">🔒</span>
            <p className="text-luxury-secondary text-sm leading-relaxed">
              TaxVault Global is committed to protecting your privacy and handling your personal and financial information with the utmost care — in full compliance with the <strong className="text-luxury-text">Protection of Personal Information Act (POPIA)</strong> of South Africa.
            </p>
          </div>
        </div>

        <Section title="1. Who We Are">
          <p>TaxVault Global ("we", "us", "our") is a South African digital platform providing tax document management and personal finance services.</p>
          <p><strong className="text-luxury-text">Email:</strong> security@taxvault.live &nbsp;|&nbsp; <strong className="text-luxury-text">Website:</strong> www.taxvault.live</p>
        </Section>

        <Section title="2. Information We Collect">
          <p>We collect the following categories of personal information:</p>
          <ul className="list-none space-y-2 mt-2">
            {[
              ["Account Information", "Full name, email address, phone number, physical address"],
              ["Tax Information", "Tax reference number, IRP5 details, SARS submission records"],
              ["Financial Information", "Budget data, income/expense records, debt and investment information you enter"],
              ["Documents", "Files and documents you upload to your vault"],
              ["Usage Data", "Log data, IP addresses, browser type, pages visited"],
              ["Payment Information", "Processed securely via PayStack — we never store card details"],
            ].map(([k, v]) => (
              <li key={k} className="flex gap-2">
                <span className="text-luxury-cta font-bold flex-shrink-0 mt-0.5">✓</span>
                <span><strong className="text-luxury-text">{k}:</strong> {v}</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section title="3. How We Use Your Information">
          <ul className="list-none space-y-2">
            {[
              "Provide, maintain and improve our platform and services",
              "Process subscription payments securely via PayStack",
              "Send important tax deadline reminders and notifications",
              "Respond to support requests and communications",
              "Comply with South African laws including POPIA and SARS regulations",
              "Prevent fraud and ensure platform security",
            ].map(item => (
              <li key={item} className="flex gap-2">
                <span className="text-luxury-cta font-bold flex-shrink-0 mt-0.5">✓</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p className="mt-4 p-3 bg-luxury-elevated border border-luxury-border rounded-xl">
            <strong className="text-luxury-text">We never sell your personal information to third parties.</strong>
          </p>
        </Section>

        <Section title="4. Data Storage & Security">
          <ul className="list-none space-y-2">
            {[
              "256-bit SSL/TLS encryption for all data in transit",
              "Encryption at rest for all stored documents and personal data",
              "Bcrypt hashing for passwords — never stored in plain text",
              "JWT token-based authentication with expiry",
              "Regular automated backups",
              "Access controls limiting who can view your data",
            ].map(item => (
              <li key={item} className="flex gap-2">
                <span className="text-luxury-gold font-bold flex-shrink-0 mt-0.5">🔒</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section title="5. Your Rights Under POPIA">
          <ul className="list-none space-y-2">
            {[
              ["Right to Access", "Request a copy of all personal information we hold about you"],
              ["Right to Correction", "Request correction of inaccurate or incomplete information"],
              ["Right to Deletion", "Request deletion of your account and associated data"],
              ["Right to Object", "Object to the processing of your personal information"],
              ["Right to Data Portability", "Request your data in a portable format"],
            ].map(([k, v]) => (
              <li key={k} className="flex gap-2">
                <span className="text-luxury-gold font-bold flex-shrink-0 mt-0.5">✦</span>
                <span><strong className="text-luxury-text">{k}:</strong> {v}</span>
              </li>
            ))}
          </ul>
          <p className="mt-3">To exercise any right, contact: <strong className="text-luxury-gold">security@taxvault.live</strong></p>
        </Section>

        <Section title="6. Cookies">
          <p>We use essential cookies to maintain your login session and platform functionality. We do not use advertising or tracking cookies.</p>
        </Section>

        <Section title="7. Third-Party Services">
          <p><strong className="text-luxury-text">PayStack:</strong> Payment processing (subject to PayStack's Privacy Policy)</p>
          <p><strong className="text-luxury-text">Cloud Hosting:</strong> Secure server infrastructure with data residency in Africa where available.</p>
        </Section>

        <Section title="8. Data Retention">
          <p>We retain your data for as long as your account is active or as required by law. Upon account deletion, personal data is removed within 30 days, except where retention is required by SARS compliance.</p>
        </Section>

        <Section title="9. Children's Privacy">
          <p>TaxVault Global is not intended for users under 18. We do not knowingly collect information from minors.</p>
        </Section>

        <Section title="10. Contact & Complaints">
          <p>For privacy-related questions, contact our Privacy Officer:</p>
          <p><strong className="text-luxury-text">Email:</strong> security@taxvault.live</p>
          <p className="mt-2">You may also lodge a complaint with the <strong className="text-luxury-text">Information Regulator of South Africa</strong> if you believe your POPIA rights have been violated.</p>
        </Section>

        <div className="flex gap-4 mt-10">
          <Link to="/terms" className="px-5 py-2.5 border border-luxury-border text-luxury-secondary rounded-xl text-sm font-semibold hover:border-luxury-gold/40 hover:text-luxury-gold transition-all">
            Terms & Conditions
          </Link>
          <Link to="/contact" className="px-5 py-2.5 bg-luxury-gold hover:bg-luxury-gold-dim text-white rounded-xl text-sm font-bold transition-all">
            Contact Us
          </Link>
        </div>
      </div>

      <PublicFooter />
    </div>
  );
}
